import type { Session } from '@supabase/supabase-js';
import { useEffect, useState } from 'react';

import { getSupabaseClient } from '@/lib/supabase';

// Subscribes to Supabase auth state and returns the current session. Same
// pattern as the dashboard's AuthProvider, distilled to a single hook because
// the backoffice has no router context to thread auth through (yet).
//
//   undefined = initial getSession() still in flight (render a loading state,
//               don't bounce to login)
//   null      = signed out
//   Session   = signed in
export const useSession = (): Session | null | undefined => {
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  useEffect(() => {
    const client = getSupabaseClient();
    let cancelled = false;

    void client.auth.getSession().then(({ data }) => {
      if (!cancelled) setSession(data.session ?? null);
    });

    const { data: subscription } = client.auth.onAuthStateChange((_event, nextSession) => {
      if (cancelled) return;
      // Supabase re-fires this on token refresh and tab focus with a fresh
      // object even when nothing changed; keep the previous reference when the
      // access token matches to avoid needless re-renders/refetches.
      setSession((prev) => {
        if (prev && nextSession && prev.access_token === nextSession.access_token) return prev;
        return nextSession ?? null;
      });
    });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, []);

  return session;
};
